import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Inbox, MessagesSquare, Kanban, MoreHorizontal, Building2, Package, Settings, ExternalLink, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useResource } from "@/hooks/use-resource";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

const TABS = [
  { to: "/", label: "Hjem", icon: Home, match: (p) => p === "/", testId: "tab-home" },
  { to: "/innboks", label: "Innboks", icon: Inbox, match: (p) => p.startsWith("/innboks"), badge: true, testId: "tab-inbox" },
  { to: "/arbeid", label: "Arbeid", icon: MessagesSquare, match: (p) => p.startsWith("/arbeid") || p.startsWith("/p/") || p === "/ny", testId: "tab-work" },
  { to: "/pipeline", label: "Tilbud", icon: Kanban, match: (p) => p.startsWith("/pipeline"), testId: "tab-pipeline" },
];
const MORE = [["/customers", "Kunder", Building2], ["/products", "Produkter", Package], ["/settings", "Innstillinger", Settings]];

/** Bottom tab bar for phones/tablets: five main entries, "Mer" holds the rest. */
export function MobileTabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const inbox = useResource("/inbox", null, { interval: 45000 });
  const moreActive = MORE.some(([to]) => pathname.startsWith(to));
  const item = "flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-medium outline-none transition-colors";
  return (
    <nav className="glass sticky bottom-0 z-30 flex h-[60px] shrink-0 items-stretch border-t border-black/[0.06] pb-[env(safe-area-inset-bottom)] lg:hidden" aria-label="Hovedmeny" data-testid="mobile-tabbar">
      {TABS.map((t) => {
        const active = t.match(pathname);
        const Icon = t.icon;
        const badge = t.badge ? inbox.data?.unread || 0 : 0;
        return (
          <NavLink key={t.to} to={t.to} className={cn(item, active ? "text-[hsl(var(--navy))]" : "text-foreground/55")} aria-current={active ? "page" : undefined} data-testid={t.testId}>
            <span className="relative"><Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.2 : 1.7} />{badge ? <span className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 font-mono text-[9.5px] font-semibold text-white" data-testid={`${t.testId}-badge`}>{badge > 99 ? "99+" : badge}</span> : null}</span>
            {t.label}
          </NavLink>
        );
      })}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={cn(item, moreActive ? "text-[hsl(var(--navy))]" : "text-foreground/55")} data-testid="tab-more"><MoreHorizontal className="h-[22px] w-[22px]" strokeWidth={moreActive ? 2.2 : 1.7} />Mer</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" sideOffset={10} className="w-56 rounded-2xl border-black/[0.06] p-1.5 shadow-float">
          {MORE.map(([to, label, Icon]) => <DropdownMenuItem key={to} onSelect={() => navigate(to)} className="h-10 cursor-pointer gap-2.5 rounded-lg px-2.5 text-[13.5px]" data-testid={`tab-more-${to.slice(1)}`}><Icon className="h-4 w-4 text-muted-foreground" /> {label}</DropdownMenuItem>)}
          <DropdownMenuSeparator className="bg-black/[0.06]" />
          <DropdownMenuItem onSelect={() => window.open("/foresporsel", "_blank", "noopener")} className="h-10 cursor-pointer gap-2.5 rounded-lg px-2.5 text-[13.5px]"><ExternalLink className="h-4 w-4 text-muted-foreground" /> Skjemaet kunden ser</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { logout(); navigate("/login"); }} className="h-10 cursor-pointer gap-2.5 rounded-lg px-2.5 text-[13.5px]"><LogOut className="h-4 w-4 text-muted-foreground" /> Logg ut</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
